import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, Stack, TextField, Typography, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import TyreDiagram from './TyreDiagram';
import chartSpan from './chartSpan';
import {
  tpms, HEALTH_LABEL, healthColour, alertLabel,
} from './api';
import { formatDateTime } from '../common/util/datetime';

// One quantity, its own scale, and the levels it is judged against.
//
// Pressure and temperature used to share a chart with an axis each. Two units on one grid means the
// reader has to work out which line belongs to which side before they can read either, and a tyre at
// 92 PSI looks like nothing in particular unless the warning level is drawn beside it. So each gets
// a chart, a legend that names the line in its own unit, and its limits as reference lines.
//
// The limits are only drawn where the vehicle has them configured; a threshold this workspace never
// set is left off rather than invented.
const TyreChart = ({
  title, points, series, unit, stroke, limits,
}) => {
  const theme = useTheme();
  const drawn = limits.filter(([, value]) => typeof value === 'number');
  const span = chartSpan(points.map((p) => p[series]), drawn.map(([, value]) => value));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{title}</Typography>
      <Box sx={{ width: '100%', height: 210 }}>
        <ResponsiveContainer>
          <LineChart data={points} margin={{ top: 10, right: 12, bottom: 4, left: -18 }}>
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
            <XAxis
              dataKey="at"
              type="number"
              domain={['dataMin', 'dataMax']}
              scale="time"
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            />
            <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} domain={span} />
            <Tooltip
              labelFormatter={(v) => formatDateTime(new Date(v).toISOString())}
              formatter={(value) => [`${value} ${unit}`, title.replace(/ \(.*/, '')]}
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
              }}
            />
            <Legend verticalAlign="bottom" height={26} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            {drawn.map(([label, value, colour, dash]) => (
              <ReferenceLine
                key={label}
                y={value}
                stroke={colour}
                strokeDasharray={dash}
                ifOverflow="extendDomain"
                label={{
                  value: `${label} ${value}`,
                  position: 'insideTopRight',
                  fill: colour,
                  fontSize: 10,
                }}
              />
            ))}
            <Line
              type="monotone"
              dataKey={series}
              name={title}
              stroke={stroke}
              dot={false}
              strokeWidth={2}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// One vehicle: the drawing, the tyre you picked, and its history.
//
// There is no map and no coordinate anywhere on this page, and not because they are hidden - the
// endpoints behind it do not carry them. A fitter can see that an inner rear tyre is at 76 PSI and
// 67 degrees and has been falling for an hour, and still has no way to learn where the lorry is.
// The vehicle is given by the shell, which reads it from the path; useParams is the fallback for
// anywhere this is mounted inside a route of its own.
const TpmsVehiclePage = ({ vehicleId }) => {
  const params = useParams();
  const id = vehicleId || params.id;
  const navigate = useNavigate();
  const theme = useTheme();
  const [vehicle, setVehicle] = useState(null);
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState(1);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = () => tpms(`/api/tpms/vehicles/${id}`, 'GET', null, controller.signal)
      .then((data) => {
        setVehicle(data);
        setError('');
        // Open on the tyre that needs attention, so the page answers the question on arrival.
        setSelected((current) => current || data.axles?.flatMap((a) => a.wheels)
          .sort((x, y) => ({ CRITICAL: 0, SENSOR_FAULT: 1, WARNING: 2, NO_DATA: 3, NORMAL: 4 }[x.health] ?? 9)
            - ({ CRITICAL: 0, SENSOR_FAULT: 1, WARNING: 2, NO_DATA: 3, NORMAL: 4 }[y.health] ?? 9))[0]?.position);
      })
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message); });
    load();
    const timer = setInterval(load, 15000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [id]);

  useEffect(() => {
    if (!selected) return undefined;
    const controller = new AbortController();
    setHistory(null);
    tpms(`/api/tpms/vehicles/${id}/tyres/${encodeURIComponent(selected)}/history?days=${days}`, 'GET', null, controller.signal)
      .then(setHistory)
      .catch(() => setHistory({ points: [] }));
    return () => controller.abort();
  }, [id, selected, days]);

  const wheels = useMemo(() => (vehicle?.axles || []).flatMap((a) => a.wheels), [vehicle]);
  const tyre = wheels.find((w) => w.position === selected);

  const acknowledge = async (alertId) => {
    setBusy(true);
    try {
      await tpms(`/api/tpms/alerts/${alertId}/acknowledge`, 'POST');
      setVehicle(await tpms(`/api/tpms/vehicles/${id}`));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!vehicle && !error) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const points = (history?.points || []).map((p) => ({
    at: Date.parse(p.at),
    pressure: p.pressure == null ? null : Number(p.pressure),
    temperature: p.temperature == null ? null : Number(p.temperature),
  }));

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tpms')} sx={{ mb: 1 }}>All vehicles</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap">
        <Typography variant="h5" fontWeight={700}>{vehicle?.vehicle}</Typography>
        {!!vehicle?.vehicleType && <Typography color="text.secondary">{vehicle.vehicleType}</Typography>}
        {!!vehicle && <Chip size="small" label={HEALTH_LABEL[vehicle.health] || vehicle.health} color={healthColour(vehicle.health)} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {`${vehicle?.layout || 'No layout'} · last reading ${vehicle?.lastUpdateAt ? formatDateTime(vehicle.lastUpdateAt) : 'never'}`}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems="flex-start">
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 420px', minWidth: 0, width: '100%' }}>
          <TyreDiagram
            axles={vehicle?.axles || []}
            pressureUnit={vehicle?.pressureUnit}
            temperatureUnit={vehicle?.temperatureUnit}
            selected={selected}
            onSelect={setSelected}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 380px', minWidth: 0, width: '100%' }}>
          {!tyre && <Typography color="text.secondary">Choose a tyre on the diagram.</Typography>}
          {!!tyre && (
            <>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography variant="h6" fontFamily="monospace">{tyre.position}</Typography>
                <Chip size="small" label={HEALTH_LABEL[tyre.health] || tyre.health} color={healthColour(tyre.health)} />
              </Stack>
              <Stack gap={0.5} sx={{ mb: 2 }}>
                {[
                  ['Pressure', tyre.pressure == null ? '—' : `${tyre.pressure} ${vehicle.pressureUnit}`],
                  ['Target', tyre.targetPressure == null ? '—' : `${tyre.targetPressure} ${vehicle.pressureUnit}`],
                  ['Temperature', tyre.temperature == null ? '—' : `${tyre.temperature}°${vehicle.temperatureUnit}`],
                  ['Sensor', tyre.sensorStatus === 'NO_SENSOR' ? 'No sensor mapped' : tyre.sensorStatus],
                  ['Last reading', tyre.lastReadingAt ? formatDateTime(tyre.lastReadingAt) : 'never'],
                ].map(([label, value]) => (
                  <Stack key={label} direction="row" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>

              {!!tyre.alerts?.length && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Open alerts</Typography>
                  <Stack gap={1}>
                    {tyre.alerts.map((alert) => (
                      <Paper key={alert.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{alertLabel(alert.type)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {`${alert.state.toLowerCase()} · since ${formatDateTime(alert.detectedAt)}`}
                            </Typography>
                          </Box>
                          {alert.state !== 'ACKNOWLEDGED' && (
                            <Button size="small" disabled={busy} onClick={() => acknowledge(alert.id)}>Acknowledge</Button>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">History</Typography>
                <TextField size="small" select value={days} onChange={(e) => setDays(Number(e.target.value))} sx={{ minWidth: 130 }}>
                  <MenuItem value={1}>24 hours</MenuItem>
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={30}>30 days</MenuItem>
                </TextField>
              </Stack>
              {!points.length && <Typography variant="body2" color="text.secondary">Nothing recorded for this tyre in that period.</Typography>}
              {!!points.length && (
                <Stack gap={2}>
                  <TyreChart
                    title={`Pressure (${vehicle.pressureUnit})`}
                    points={points}
                    series="pressure"
                    unit={vehicle.pressureUnit}
                    stroke={theme.palette.primary.main}
                    limits={[
                      ['Target', history?.thresholds?.targetPressure, theme.palette.success.main, '6 4'],
                      ['Warning low', history?.thresholds?.warningLowPressure, theme.palette.warning.main, '4 4'],
                      ['Critical low', history?.thresholds?.criticalLowPressure, theme.palette.error.main, '4 4'],
                      ['Warning high', history?.thresholds?.warningHighPressure, theme.palette.warning.main, '4 4'],
                      ['Critical high', history?.thresholds?.criticalHighPressure, theme.palette.error.main, '4 4'],
                    ]}
                  />
                  <TyreChart
                    title={`Temperature (\u00b0${vehicle.temperatureUnit})`}
                    points={points}
                    series="temperature"
                    unit={`\u00b0${vehicle.temperatureUnit}`}
                    stroke={theme.palette.warning.dark}
                    limits={[
                      ['Warning', history?.thresholds?.warningTemperature, theme.palette.warning.main, '4 4'],
                      ['Critical', history?.thresholds?.criticalTemperature, theme.palette.error.main, '4 4'],
                    ]}
                  />
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default TpmsVehiclePage;
